import React from 'react';
import PropTypes from 'prop-types';
import { AffordableTab } from './Affordable';
import { PhotosTab } from './Photos';
import PropertyInfoEditBasics from './PropertyInfoEditBasics';
import PropertyInfoEditAmenities from './PropertyInfoEditAmenities';
import PropertyInfoEditLeasing from './Leasing/PropertyInfoEditLeasing';
import PropertyInfoEditNearbyCommunities from './PropertyInfoEditNearbyCommunities';
import { PropertyInfoEditSettings } from './PropertyInfoEditSettings';
import { useAppContext } from '../../Context/AppContext';
import { useFeatureFlagContext } from '../../Context/FeatureFlagContext';
import { ACCESS_SETTINGS } from '../../constants';

export const AFFORDABLE_TAB_OPTION = 'Affordable';
export const CROSS_SELL_TAB_OPTION = 'Cross Sell';
export const PHOTOS_TAB_OPTION = 'Photos';

const PropertyInfo = ({
  changeSelectedTab,
  onInformationChanged,
  property,
  saveAffordable,
  savePropertyPreference,
  selectedTab,
  settings,
  teams,
  updatePropertyLogo,
  updatePropertyPhotos,
  updatePropertyPreference,
}) => {
  const { isAuthorized } = useAppContext();

  const { isAdminPhotosTabEnabled, isAffordableLeasingEnabled } =
    useFeatureFlagContext();

  const isCrossSellAccessEnabled = isAuthorized(ACCESS_SETTINGS.CROSS_SELL_TAB);
  const isPhotosTabAccessEnabled =
    isAdminPhotosTabEnabled && isAuthorized(ACCESS_SETTINGS.PHOTOS_PAGE);

  const isAffordableTabEnabled =
    isAffordableLeasingEnabled &&
    property.preferences.enable_affordable_housing;

  const getActiveStatusSubClassOfTab = (tabOption) => {
    return selectedTab === tabOption ? 'active' : 'inactive';
  };

  const TAB_OPTIONS = [
    'Basics',
    'Amenities',
    'Leasing',
    'Settings',
    isPhotosTabAccessEnabled && PHOTOS_TAB_OPTION,
    isCrossSellAccessEnabled && CROSS_SELL_TAB_OPTION,
    isAffordableTabEnabled && AFFORDABLE_TAB_OPTION,
  ].filter(Boolean);

  return (
    <div>
      <h2>{property.data.location.name}</h2>

      <div className="property-info-nav-tabs-menu" role="tablist">
        {TAB_OPTIONS.map((tabOption) => (
          <div
            key={tabOption}
            className={`property-info-nav-tab ${getActiveStatusSubClassOfTab(
              tabOption
            )}`}
            onClick={() => {
              changeSelectedTab(tabOption);
            }}
            role="tab"
          >
            {tabOption}
          </div>
        ))}
      </div>

      {selectedTab === 'Basics' && (
        <PropertyInfoEditBasics
          teams={teams}
          property={property}
          onChange={onInformationChanged}
        />
      )}

      {selectedTab === 'Amenities' && (
        <PropertyInfoEditAmenities
          property={property}
          onChange={onInformationChanged}
        />
      )}

      {selectedTab === 'Leasing' && (
        <PropertyInfoEditLeasing
          property={property}
          onChange={onInformationChanged}
        />
      )}

      {selectedTab === 'Settings' && (
        <PropertyInfoEditSettings
          key={property.id}
          propertyPreferences={property.preferences || {}}
          savePropertyPreference={savePropertyPreference}
          updatePropertyPreference={updatePropertyPreference}
        />
      )}

      {selectedTab === PHOTOS_TAB_OPTION && (
        <PhotosTab
          key={property.id}
          property={property}
          updatePropertyLogo={updatePropertyLogo}
          updatePropertyPhotos={updatePropertyPhotos}
        />
      )}

      {isCrossSellAccessEnabled && selectedTab === CROSS_SELL_TAB_OPTION && (
        <PropertyInfoEditNearbyCommunities
          property={property}
          settings={settings}
          onChange={onInformationChanged}
        />
      )}

      {isAffordableTabEnabled && selectedTab === AFFORDABLE_TAB_OPTION && (
        <AffordableTab
          affordable={property.data.affordable}
          key={property.id}
          saveAffordable={saveAffordable}
        />
      )}
    </div>
  );
};

PropertyInfo.propTypes = {
  changeSelectedTab: PropTypes.func,
  onInformationChanged: PropTypes.func,
  property: PropTypes.object,
  saveAffordable: PropTypes.func,
  settings: PropTypes.array,
  selectedTab: PropTypes.string,
  savePropertyPreference: PropTypes.func,
  teams: PropTypes.array,
  updatePropertyLogo: PropTypes.func,
  updatePropertyPhotos: PropTypes.func,
  updatePropertyPreference: PropTypes.func,
};

export default PropertyInfo;
